/**
 * El equipo de un proyecto.
 *
 * Sustituye a `TeamManager`, que era una maqueta: cuatro personas escritas a
 * mano, ningún estado y ninguna llamada. Aquí todo es real —invitar manda un
 * correo, cambiar el rol cambia lo que el servidor deja hacer— y la pantalla
 * está construida alrededor de la única pregunta que la gente trae: «¿quién
 * entra a mi sitio y qué puede tocar?».
 *
 * De ahí las dos decisiones de forma:
 *
 * 1. El dueño va en la MISMA lista que los demás, marcado. Separarlo en su
 *    propia sección obliga a leer dos listas para responder esa pregunta.
 * 2. Cada rol enseña, ahí mismo, a qué secciones abre. Un selector que sólo
 *    dice «Diseño» obliga a adivinar, y quien invita acaba dando administrador
 *    «por si acaso», que es como se reparten permisos de más.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useI18n, translateServerError } from '../../../lib/i18n'
import { MASCOT_WAVE_CENTER, MASCOT_WAVE_SIZES, MascotOutlinePattern } from '../../MascotOutlinePattern'
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconCrown,
  IconInfoCircle,
  IconLoader2,
  IconLogout,
  IconLock,
  IconMail,
  IconPlus,
  IconTrash,
  IconUserPlus,
  IconUsers,
  IconX,
} from '@tabler/icons-react'
import {
  PROJECT_ROLES,
  PROJECT_ROLE_ORDER,
  sectionAccessForRole,
  can,
  roleLabelKey,
  type ProjectAccessRole,
  type ProjectRole,
} from '@webbuilder/shared-types'
import { ProfileAvatarContent } from '@webbuilder/ui'
import { cancelTeamInvite, changeTeamRole, fetchTeam, inviteToTeam, removeTeamMember, type TeamSnapshot } from '../../../lib/api'
import { useToast } from '../../Toast'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { ConfirmDialog } from '../ConfirmDialog'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { ROLE_ICONS } from '../../../lib/roleIcons'

/** Microcopy específica de Equipo: precisa, paralela y orientada al alcance. */
/* Claves, no texto: es una constante de módulo y ahí no se puede llamar a un
   hook. Quien la usa la pasa por su propio `t`. */
const ROLE_DESCRIPTION: Record<ProjectRole, string> = {
  admin: 'role.admin.description',
  editor: 'role.editor.description',
  design: 'role.design.description',
  content: 'role.content.description',
  finance: 'role.finance.description',
  viewer: 'role.viewer.description',
}


/**
 * Lo que un rol abre.
 *
 * `withDenied` decide si además se enseña, tachado, lo que NO abre. Los dos
 * sitios que usan esta lista preguntan cosas distintas y por eso no llevan lo
 * mismo:
 *
 *   · Al INVITAR se está decidiendo, y ahí lo excluido es la mitad útil del
 *     dato: «abre lo que necesito» sólo se confirma viendo que además no abre
 *     los pedidos. Sin eso se acaba dando administrador por si acaso.
 *   · En el CATÁLOGO no se decide nada, se consulta. Seis tarjetas repitiendo
 *     las quince secciones —la mayoría tachadas— convierten una referencia en
 *     un muro de texto donde lo que el rol sí hace queda enterrado.
 */
function RoleAccessList({ role, visibleLabels = false }: { role: ProjectAccessRole; visibleLabels?: boolean }) {
  const { t } = useI18n()
  const labelBase = useId()
  /* Las etiquetas que devuelve son CLAVES: los nombres de sección se comparten
     con el editor y el worker, y cada uno pinta en su idioma. */
  const rows = sectionAccessForRole(role)
  const allowed = rows.filter((row) => row.allowed)
  const denied = rows.filter((row) => !row.allowed)

  return (
    <div className="dash-team-role-access">
      <div className="dash-team-role-access-col">
        {/* Los rótulos son para quien usa lector de pantalla, que no ve las dos
            columnas ni distingue una palomita de una equis: sin ellos, las
            quince secciones llegan como una lista corrida sin decir cuál es
            cuál. En pantalla los tapa `dash-sr-only` porque el símbolo y la
            posición ya lo dicen. */}
        <p className={visibleLabels ? 'dash-team-role-access-title is-included' : 'dash-sr-only'} id={`${labelBase}-yes`}>{t('team.role.includes')}</p>
        <ul className="dash-team-role-preview-list" aria-labelledby={`${labelBase}-yes`}>
          {allowed.map(({ label }) => (
            <li key={label}><IconCheck size={12} aria-hidden="true" />{t(label)}</li>
          ))}
        </ul>
      </div>

      {denied.length > 0 ? (
        <div className="dash-team-role-access-col">
          <p className={visibleLabels ? 'dash-team-role-access-title is-denied' : 'dash-sr-only'} id={`${labelBase}-no`}>{t('team.role.excludes')}</p>
          <ul className="dash-team-role-preview-list" aria-labelledby={`${labelBase}-no`}>
            {denied.map(({ label }) => (
              <li key={label} className="is-denied"><IconX size={12} aria-hidden="true" />{t(label)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Una referencia compacta, no una acción.
 *
 * Nombre y descripción bastan para recorrer los seis roles. Las quince
 * secciones aparecen sólo cuando alguien pide el detalle; así la guía informa
 * sin convertirse en el bloque más pesado de una pantalla cuyo trabajo
 * principal sigue siendo administrar personas e invitaciones.
 */
function RoleGuideCard({ role }: { role: ProjectRole }) {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState(false)
  const Icon = ROLE_ICONS[role]
  const detailId = `team-role-guide-${role}`
  const toggleExpanded = () => setExpanded((open) => !open)

  return (
    <article className={`dash-team-role-guide${expanded ? ' is-expanded' : ''}`}>
      <div
        className="dash-team-role-guide-head"
        role="button"
        tabIndex={0}
        aria-label={t('role.togglePermissions', {
          action: t(expanded ? 'role.hide' : 'role.show'),
          role: t(PROJECT_ROLES[role].labelKey),
        })}
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleExpanded()
          }
        }}
      >
        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-team-role-guide-icon" aria-hidden="true">
          <Icon size={16} />
        </span>
        <div className="dash-team-role-guide-copy">
          <h4>{t(PROJECT_ROLES[role].labelKey)}</h4>
          <p>{t(ROLE_DESCRIPTION[role])}</p>
        </div>
        <span className="dash-team-role-guide-toggle" aria-hidden="true">
          <IconPlus size={15} aria-hidden="true" />
        </span>
      </div>

      {expanded ? (
        <div className="dash-team-role-guide-detail" id={detailId}>
          <RoleAccessList role={role} visibleLabels />
          {role === 'admin' ? (
            <p className="dash-team-role-card-caveat">
              <IconLock size={13} aria-hidden="true" />
              {t('team.ownerOnly')}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

/**
 * La ficha de un rol: símbolo, nombre, qué hace y a qué abre.
 *
 * Esta variante completa aparece al elegir un rol. La guía del pie usa una
 * versión plegable, porque allí se comparan seis y no conviene enseñar las
 * quince secciones de todos a la vez.
 */
function RoleCard({ role }: { role: ProjectRole }) {
  const { t } = useI18n()
  const Icon = ROLE_ICONS[role]
  return (
    <article className="dash-team-role-card">
      {/* En el acento del panel y no en el tono de su área: los seis roles no son
          seis categorías que comparar —sólo hay uno elegido a la vez— y seis
          colores distintos ahí obligaban a aprender un código para leer una
          ficha sola. Es el mismo criterio que ya se aplicó a la pastilla del
          nombre y a la marca del proyecto. */}
      <span className="dash-widget-glass-icon dash-team-role-icon" aria-hidden="true">
        <Icon size={17} />
      </span>
      <h4>{t(PROJECT_ROLES[role].labelKey)}</h4>
      <p className="dash-team-role-card-summary">{ROLE_DESCRIPTION[role]}</p>
      <RoleAccessList role={role} />
      {/* Sólo en Administrador, porque es el único rol en el que cabe la duda:
          «todo el proyecto» suena a todo, y hay tres cosas que no. En los demás
          sería una advertencia sobre algo que nadie estaba a punto de hacer. */}
      {role === 'admin' ? (
        <p className="dash-team-role-card-caveat">
          <IconLock size={13} aria-hidden="true" />
              {t('team.ownerOnly')}
        </p>
      ) : null}
    </article>
  )
}

function RoleChip({ role }: { role: ProjectAccessRole }) {
  const { t } = useI18n()
  const Icon = role === 'owner' ? IconCrown : ROLE_ICONS[role]
  return (
    <span className={`dash-team-role-chip${role === 'owner' ? ' is-owner' : ''}`}>
      <Icon size={12} aria-hidden="true" />
      {t(roleLabelKey(role))}
    </span>
  )
}

/** Un solo control para leer y cambiar el rol, sin botón y selector separados. */
function RolePicker({ value, disabled, changed, onChange }: {
  value: ProjectRole
  disabled?: boolean
  changed?: boolean
  onChange: (role: ProjectRole) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const CurrentIcon = ROLE_ICONS[value]

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <div className={`dash-team-role-picker${open ? ' is-open' : ''}${changed ? ' is-changed' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="dash-team-role-picker-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((visible) => !visible)}
      >
        <CurrentIcon size={13} aria-hidden="true" />
        <span>{t(PROJECT_ROLES[value].labelKey)}</span>
        <IconChevronDown size={13} aria-hidden="true" />
      </button>

      {open ? (
        <div className="dash-team-role-picker-menu" role="menu" aria-label={t('team.pickRoleAria')}>
          {PROJECT_ROLE_ORDER.map((projectRole) => {
            const OptionIcon = ROLE_ICONS[projectRole]
            const selected = projectRole === value
            return (
              <button
                type="button"
                className={`dash-team-role-picker-option${selected ? ' is-selected' : ''}`}
                role="menuitemradio"
                aria-checked={selected}
                key={projectRole}
                onClick={() => {
                  if (!selected) onChange(projectRole)
                  setOpen(false)
                }}
              >
                <span className="dash-team-role-picker-option-icon" aria-hidden="true"><OptionIcon size={14} /></span>
                <span className="dash-team-role-picker-option-copy">
                  <strong>{t(PROJECT_ROLES[projectRole].labelKey)}</strong>
                  <small>{t(ROLE_DESCRIPTION[projectRole])}</small>
                </span>
                {selected ? <IconCheck size={14} className="dash-team-role-picker-check" aria-hidden="true" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function Avatar({ name, email, url }: { name: string; email: string; url: string | null }) {
  return (
    <span className="dash-team-avatar" aria-hidden="true">
      {url
        ? <img src={url} alt="" />
        : <ProfileAvatarContent name={name || email || '?'} />}
    </span>
  )
}

/**
 * El selector de rol del formulario de invitar.
 *
 * No es un `select` nativo, por el mismo motivo que el de servicio en «Nueva
 * cita»: cada navegador le pone su propia flecha y su propio relleno encima
 * del estilo, así que un campo con el resto de la tarjeta alrededor —fondo,
 * texto, tamaño— acaba siendo el único que no lo tiene. El menú propio se ve
 * igual en todos.
 *
 * `placeholder` deja el campo SIN elegir de fábrica: al traer «Editor» puesto
 * de fábrica, invitar sin mirar el selector daba un rol que nadie había
 * decidido —y hacia arriba, que es el error caro—.
 */
function RoleSelect({ value, onChange, disabled, id, placeholder }: {
  value: ProjectRole | ''
  onChange: (role: ProjectRole | '') => void
  disabled?: boolean
  id?: string
  placeholder?: string
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <div className={`dash-team-invite-role-field${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`dash-team-invite-role-select${value === '' ? ' is-placeholder' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((visible) => !visible)}
      >
        <span>{value ? t(PROJECT_ROLES[value].labelKey) : placeholder}</span>
        <IconChevronDown size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="dash-team-invite-role-options" role="listbox" aria-label={placeholder}>
          {PROJECT_ROLE_ORDER.map((role) => {
            const selected = role === value
            return (
              <button
                key={role}
                type="button"
                role="option"
                aria-selected={selected}
                className={selected ? 'is-selected' : ''}
                onClick={() => { onChange(role); setOpen(false) }}
              >
                <span>{t(PROJECT_ROLES[role].labelKey)}</span>
                {selected ? <IconCheck size={16} aria-hidden="true" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export function TeamSection({ projectId, token, projectName, onLeft, onOpenPlans }: {
  projectId: string
  token: string
  /** Para poder decir de QUÉ te estás saliendo en el diálogo. */
  projectName?: string
  /** Al salirse: quien la monta decide adónde ir, porque el proyecto ya no está. */
  onLeft?: () => void
  /** Abre la pantalla donde se puede contratar un plan con colaboradores. */
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  /* El acuse va por el mismo aviso flotante que el resto del panel. Sin él,
     invitar y quitar sólo se notan por lo que cambia en la lista —una fila más,
     una menos— y eso es fácil de no ver cuando la lista tiene diez. */
  const { showToast } = useToast()
  const [snapshot, setSnapshot] = useState<TeamSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  /* Sin rol de fábrica. Ver `RoleSelect`. */
  const [role, setRole] = useState<ProjectRole | ''>('')
  const [sending, setSending] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  /* El enlace se enseña una sola vez, tras invitar. Es el respaldo cuando el
     correo no llega —y en local, sin clave de Resend, la única vía—. */
  const [lastLink, setLastLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  /**
   * Lo que está esperando un «seguro?».
   *
   * Quitar a alguien no se deshace: hay que volver a invitarle, esperar a que
   * acepte, y mientras tanto perdió el acceso. Es la clase de acción que no
   * puede depender de no haber fallado el clic —los botones de fila están a
   * unos píxeles del selector de rol—.
   *
   * Guarda la fila entera y no sólo su id: el diálogo dice a QUIÉN vas a
   * quitar, y con el id habría que volver a buscarla para escribir su nombre.
   */
  /**
   * Los roles cambiados y todavía sin guardar, por id de miembro.
   *
   * Antes cada cambio del desplegable salía disparado al servidor. Eso hace que
   * un rol se aplique mientras aún estás decidiendo —bajar de Administrador a
   * Lectura para volver a subir pasa por dejar a esa persona sin acceso un
   * instante— y no deja deshacer: la única forma de volver atrás era acordarse
   * de cuál era el rol anterior. Con borrador hay un paso donde arrepentirse.
   */
  const [draftRoles, setDraftRoles] = useState<Record<string, ProjectRole>>({})
  const [savingRoles, setSavingRoles] = useState(false)

  const [pendingRemoval, setPendingRemoval] = useState<
    | { kind: 'member'; id: string; name: string }
    | { kind: 'invite'; id: string; name: string }
    | { kind: 'leave'; id: string; name: string }
    | null
  >(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setSnapshot(await fetchTeam(projectId, token))
    } catch (err) {
      setError(translateServerError(err, t('team.loadFailed')))
    } finally {
      setLoading(false)
    }
    /* `t` porque el mensaje de error se arma aquí: sin ella quedaba escrito en el
       idioma que hubiera al montar. */
  }, [projectId, token, t])

  useEffect(() => { void load() }, [load])

  /* Equipo llegó después que el loader compartido y aún conservaba su propio
     texto con spinner. Mantener esta capa hasta que la mascota termina de salir
     evita el destello del contenido al resolver la petición. */
  const teamLoader = useRecordLoader(loading)

  const manages = snapshot ? can(snapshot.myRole, 'team.manage') : false
  const seats = snapshot?.seats
  const full = seats ? seats.limit !== null && seats.used >= seats.limit : false
  /* El plan no trae asientos: no es que estén ocupados, es que no los hay. Son
     dos pantallas distintas —una vende y la otra pide liberar sitio— y por eso
     tiene nombre propio en vez de repetir la comparación por todo el JSX. */
  const noSeats = seats?.limit === 0

  async function submitInvite(event: React.FormEvent) {
    event.preventDefault()
    if (!email.trim() || !role || sending) return
    setSending(true)
    setInviteError(null)
    setLastLink(null)
    try {
      const invite = await inviteToTeam(projectId, token, { email: email.trim(), role })
      showToast(t('team.inviteSentTo', { email: invite.email }))
      setEmail('')
      setLastLink(`${window.location.origin}/equipo/${invite.token}`)
      await load()
    } catch (err) {
      /* El mensaje del servidor, no uno genérico: distingue «ya está invitado»
         de «tu plan no da para más», y son acciones distintas. */
      setInviteError(translateServerError(err, t('team.inviteFailed')))
    } finally {
      setSending(false)
    }
  }

  /* Un borrador que coincide con lo que ya hay no es un cambio: volver al rol
     original con el desplegable tiene que APAGAR la barra, no dejarla encendida
     con un cambio que no cambia nada. */
  const changedRoles = (snapshot?.members ?? []).filter(
    (member) => draftRoles[member.memberId] && draftRoles[member.memberId] !== member.role,
  )
  const dirty = changedRoles.length > 0

  async function saveRoles() {
    if (!dirty || savingRoles) return
    setSavingRoles(true)
    setError(null)
    try {
      /* En serie y no en paralelo: son pocas y cada una responde con el estado
         entero; lanzadas a la vez, la última en llegar podía pintar una lista
         que no incluía a las demás. */
      for (const member of changedRoles) {
        const next = draftRoles[member.memberId]
        if (next) await changeTeamRole(projectId, member.memberId, next, token)
      }
      setDraftRoles({})
      showToast(changedRoles.length === 1
        ? t('team.roleSavedOne')
        : t('team.roleSavedMany', { count: changedRoles.length }))
      await load()
    } catch (err) {
      setError(translateServerError(err, t('team.roleSaveFailed')))
    } finally {
      setSavingRoles(false)
    }
  }

  async function act(id: string, run: () => Promise<unknown>) {
    setBusyId(id)
    try {
      await run()
      await load()
    } catch (err) {
      setError(translateServerError(err, t('team.actionFailed')))
    } finally {
      setBusyId(null)
    }
  }

  if (teamLoader.visible) return <SectionLoader loader={teamLoader} label={t('team.loading')} />

  const people = snapshot
    ? [{ kind: 'owner' as const, person: snapshot.owner }, ...snapshot.members.map((m) => ({ kind: 'member' as const, person: m }))]
    : []

  return (
    /* El mismo reparto que Configuración: copia de apoyo fija a la izquierda y
       las tarjetas a la derecha. Se reutilizan sus clases en vez de escribir
       otro reparto igual — son la misma clase de pantalla, y con dos rejillas
       distintas acabarían separándose sin que nadie lo decidiera. */
    <div className="dash-project-general-layout dash-team-page">
      <header className="dash-project-general-intro">
        <div className="dash-header">
          <h1>{t('team.title')}</h1>
          <p>
            {t('team.intro')}
          </p>
        </div>

        {/* Los asientos, en la columna de apoyo y no en la cabecera de la
            tarjeta de invitar. Ahí dentro contestaban «¿puedo invitar a otro?»,
            que era la pregunta del formulario; aquí contestan «¿cuánto me
            queda?», que es un dato del proyecto y se queda a la vista mientras
            se recorre la lista. */}
        {seats && seats.limit !== 0 ? (
          <p className={`dash-team-seats-note${full ? ' is-full' : ''}`}>
            <IconUsers size={14} aria-hidden="true" />
            {seats.limit === null
              ? t(seats.used === 1 ? 'team.seatsUnlimitedOne' : 'team.seatsUnlimitedMany', { count: seats.used })
              : t(seats.limit === 1 ? 'team.seatsUsedOne' : 'team.seatsUsedMany', {
                  used: seats.used,
                  limit: seats.limit,
                })}
          </p>
        ) : null}
      </header>

      <div className="dash-project-general-content">
      {error ? (
        <div className="dash-team-alert" role="alert">
          <IconAlertTriangle size={15} aria-hidden="true" />
          {error}
        </div>
      ) : null}

      {/* ── Oferta de ampliación de plan (cuando no hay asientos) ────────── */}
      {manages && noSeats ? (
        <div className="dash-team-upsell is-offer">
          <div>
            {/* El rótulo de la oferta, como en el banner de planes: dice de qué
                va antes que el titular, y a este tamaño es lo que distingue una
                promoción de un aviso del sistema. */}
            <span className="dash-upsell-lock" aria-hidden="true"><IconLock size={16} /></span>
            <span className="bento-eyebrow">{t('team.upsell.eyebrow')}</span>
            <strong>{t('team.upsell.title')}</strong>
            <p>{t('team.upsell.body')}</p>
            <ul className="dash-team-upsell-points">
              {['team.upsell.point1', 'team.upsell.point2', 'team.upsell.point3'].map((key) => (
                <li key={key}><IconCheck size={13} aria-hidden="true" /> {t(key)}</li>
              ))}
            </ul>
          </div>
          <MascotOutlinePattern className="dash-team-upsell-pattern" eyes={false} sizes={MASCOT_WAVE_SIZES} center={MASCOT_WAVE_CENTER} />
          <button className="dash-primary-btn dash-team-upsell-cta" type="button" onClick={onOpenPlans}>
            {t('team.upgradeCta')}
            <IconArrowUpRight size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {/* ── Quién entra ─────────────────────────────────────────────────── */}
      <section className="dash-settings-card dash-team-list-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconUsers size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('team.membersTitle')}</h3>
            <p>{t('team.membersHint')}</p>
          </div>
        </div>

        {/* La misma barra que el resto del panel, y no un botón propio: guardar
            es guardar en todas partes, y una pantalla con su propia forma de
            hacerlo obliga a aprenderla. Se enseña sola cuando hay algo que
            guardar; `PendingChangesActions` devuelve `null` si no lo hay. */}
        <PendingChangesActions
          dirty={dirty}
          saving={savingRoles}
          savingLabel={t('team.savingRoles')}
          onDiscard={() => setDraftRoles({})}
          onSave={() => { void saveRoles() }}
        />

        <ul className="dash-team-people">
          {people.map(({ kind, person }) => {
            const isOwner = kind === 'owner'
            const memberId = isOwner ? null : person.memberId
            const busy = busyId === memberId
            const isMe = !isOwner && person.id === snapshot?.myUserId
            /* El rol que se está eligiendo, sólo mientras difiera del guardado.
               Es lo que decide si la fila abre su ficha: sin cambio no hay nada
               que explicar, y una ficha por fila convertiría la lista en un
               muro. */
            const draft = memberId && draftRoles[memberId] !== person.role
              ? draftRoles[memberId]
              : undefined
            const displayedRole = isOwner ? person.role : (draft ?? person.role)
            return (
              <li key={isOwner ? 'owner' : memberId} className={`dash-team-person${draft ? ' is-changing' : ''}`}>
                <Avatar name={person.name} email={person.email} url={person.avatarUrl} />
                <div className="dash-team-person-copy">
                  <span className="dash-team-person-name">
                    {person.name || person.email || t('team.unnamed')}
                  </span>
                  <span className="dash-team-person-email">{person.email}</span>
                </div>

                <div className="dash-team-person-role">
                  {!isOwner && memberId && manages ? (
                    <RolePicker
                      value={displayedRole as ProjectRole}
                      disabled={busy || savingRoles}
                      changed={Boolean(draft)}
                      onChange={(next) => {
                        setDraftRoles((current) => ({ ...current, [memberId]: next }))
                      }}
                    />
                  ) : (
                    <RoleChip role={displayedRole} />
                  )}
                </div>

                <div className="dash-team-person-actions">
                  {/* Tu propia fila ofrece SALIR, no «quitar».
                      Es la misma fila de la tabla y la misma llamada, pero no la
                      misma acción: quitar es algo que le haces a otro y salir es
                      una decisión sobre ti. Con el mismo icono de papelera para
                      las dos, salirse parecía una operación de administrador y
                      quien no lo era daba por hecho que no podía.

                      Y no depende de `manages`: quedarse atrapado en el proyecto
                      de alguien porque tu rol es «lectura» no tendría sentido. El
                      servidor opina igual — para salir sólo pide estar dentro. */}
                  {!isOwner && memberId && isMe ? (
                    <button
                      className="dash-icon-btn dash-team-leave"
                      type="button"
                      aria-label={t('team.leaveAria')}
                      data-tooltip={t('team.leaveAria')}
                      disabled={busy}
                      onClick={() => setPendingRemoval({
                        kind: 'leave',
                        id: memberId,
                        name: projectName || t('team.projectFallback'),
                      })}
                    >
                      {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconLogout size={15} />}
                    </button>
                  ) : null}

                  {!isOwner && memberId && !isMe && manages ? (
                    <button
                      className="dash-icon-btn dash-team-remove"
                      type="button"
                      aria-label={t('team.revokeAria', { name: person.name || person.email || t('team.memberFallback') })}
                      disabled={busy}
                      onClick={() => setPendingRemoval({
                        kind: 'member',
                        id: memberId,
                        name: person.name || person.email || t('team.memberFallback'),
                      })}
                    >
                      {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconTrash size={15} />}
                    </button>
                  ) : null}
                </div>

              </li>
            )
          })}
        </ul>
      </section>

      {/* ── Invitaciones sin responder ──────────────────────────────────── */}
      {snapshot && snapshot.invites.length > 0 ? (
        <section className="dash-settings-card dash-team-list-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
              <IconMail size={18} />
            </span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('team.invitesTitle', { count: snapshot.invites.length })}</h3>
              <p>{t('team.invitesHint')}</p>
            </div>
          </div>

          <ul className="dash-team-people">
            {snapshot.invites.map((invite) => {
              const busy = busyId === invite.id
              const days = Math.max(
                0,
                Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / 86_400_000),
              )
              return (
                <li key={invite.id} className="dash-team-person is-pending">
                  <span className="dash-team-avatar is-pending" aria-hidden="true"><IconMail size={15} /></span>
                  <div className="dash-team-person-copy">
                    <span className="dash-team-person-name">{invite.email}</span>
                    <span className="dash-team-person-email">
                      {days === 1 ? t('team.expiresInOne') : t('team.expiresInDays', { days })}
                    </span>
                  </div>
                  <RoleChip role={invite.role} />
                  <div className="dash-team-person-actions">
                    {manages ? (
                      <button
                        className="dash-icon-btn dash-team-remove"
                        type="button"
                        aria-label={t('team.cancelInviteFor', { email: invite.email })}
                        disabled={busy}
                        onClick={() => setPendingRemoval({ kind: 'invite', id: invite.id, name: invite.email })}
                      >
                        {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconX size={15} />}
                      </button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}

      {/* ── Invitar ─────────────────────────────────────────────────────── */}
      {manages && !noSeats ? (
        <section className="dash-settings-card dash-team-invite-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
              <IconUserPlus size={18} />
            </span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('team.inviteTitle')}</h3>
              <p>{t('team.inviteHint')}</p>
            </div>
          </div>

          {/* Con asientos pero llenos sí es un aviso: ya se paga, y la salida
              puede ser liberar uno en vez de comprar. Se queda con su triángulo
              ámbar dentro de la tarjeta, sobre el formulario que desbloquea. */}
          {full ? (
            <div className="dash-team-upsell">
              <IconAlertTriangle size={15} aria-hidden="true" />
              <div>
                <strong>{t('team.seatLimit')}</strong>
                <p>{t('team.freeUpSeats')}</p>
              </div>
              <button className="dash-primary-btn dash-team-upsell-cta" type="button" onClick={onOpenPlans}>
                {t('team.upgradeCta')}
                <IconArrowUpRight size={14} aria-hidden="true" />
              </button>
            </div>
          ) : (
            <form className="dash-team-invite-form" onSubmit={(event) => { void submitInvite(event) }}>
              <label className="dash-field dash-team-invite-email">
                <span>{t('team.emailLabel')}</span>
                <div className="dash-team-input-wrap">
                  <IconMail size={15} aria-hidden="true" />
                  <input
                    className="dash-form-control dash-form-control--with-icon"
                    type="email"
                    required
                    value={email}
                    placeholder={t('team.emailPlaceholder')}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </label>

              <label className="dash-field dash-team-invite-role" htmlFor="team-invite-role">
                <span>{t('team.roleLabel')}</span>
                <RoleSelect
                  id="team-invite-role"
                  value={role}
                  onChange={setRole}
                  placeholder={t('team.rolePlaceholder')}
                />
              </label>

              <button
                className="dash-primary-btn dash-team-invite-submit"
                type="submit"
                disabled={sending || !role}
              >
                {sending ? <IconLoader2 size={15} className="dash-spin" /> : <IconPlus size={15} />}
                {sending ? t('team.sendingInvite') : t('team.sendInvite')}
              </button>

              {/* Qué abre el rol elegido. Va PEGADO al selector y no en una
                  tabla aparte: es la información que decide el rol, y a dos
                  clicks de distancia no la mira nadie. */}
              {/* La ficha del rol elegido, la misma que era la rejilla de
                  referencia. Con nada elegido no se pinta un hueco reservado: el
                  formulario se queda en su fila y crece al marcar. */}
              {role ? (
                <div className="dash-team-role-preview">
                  <RoleCard role={role} />
                </div>
              ) : null}
            </form>
          )}

          {inviteError ? (
            <p className="dash-team-invite-error" role="alert">
              <IconAlertTriangle size={14} aria-hidden="true" /> {inviteError}
            </p>
          ) : null}

          {lastLink ? (
            <div className="dash-team-link">
              <div>
                <strong>{t('team.inviteSent')}</strong>
                <p>{t('team.inviteSentHint')}</p>
              </div>
              <button
                className="dash-secondary-btn"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(lastLink).then(() => {
                    setCopied(true)
                    window.setTimeout(() => setCopied(false), 2000)
                  })
                }}
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                {copied ? t('team.copied') : t('team.copyLink')}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* ── Guía de roles ────────────────────────────────────────────────
          Referencia al final, después de las personas y de la invitación. No
          tiene controles que cambien nada: el único botón revela información. */}
      <section className="dash-settings-card dash-team-roles-guide-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconInfoCircle size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('team.rolesTitle')}</h3>
            <p>{t('team.rolesHint')}</p>
          </div>
        </div>

        <div className="dash-team-role-guide-list">
          {PROJECT_ROLE_ORDER.map((projectRole) => <RoleGuideCard role={projectRole} key={projectRole} />)}
        </div>
      </section>

      </div>

      {pendingRemoval ? (
        <ConfirmDialog
          tone="danger"
          title={
            pendingRemoval.kind === 'leave' ? t('team.leaveAria')
              : pendingRemoval.kind === 'member' ? t('team.revokeTitle')
                : t('team.cancelInviteAria')
          }
          message={pendingRemoval.kind === 'leave' ? (
            <>
              {t('team.leave.body', { project: '\uE000' })
                  .split('\uE000')
                  .flatMap((chunk, index) => (index === 0
                    ? [chunk]
                    : [<strong key="n">{pendingRemoval.name}</strong>, chunk]))}
              {t('team.workStays')}
            </>
          ) : pendingRemoval.kind === 'member' ? (
            <>
              {t('team.remove.body', { name: '\uE000' })
                  .split('\uE000')
                  .flatMap((chunk, index) => (index === 0
                    ? [chunk]
                    : [<strong key="n">{pendingRemoval.name}</strong>, chunk]))}
            </>
          ) : (
            <>
              {t('team.cancelInvite.body', { name: '\uE000' })
                  .split('\uE000')
                  .flatMap((chunk, index) => (index === 0
                    ? [chunk]
                    : [<strong key="n">{pendingRemoval.name}</strong>, chunk]))}
            </>
          )}
          confirmLabel={
            pendingRemoval.kind === 'leave' ? t('team.leaveAria')
              : pendingRemoval.kind === 'member' ? t('team.revokeTitle')
                : t('team.cancelInvite')
          }
          confirmingLabel={
            pendingRemoval.kind === 'leave' ? t('team.leaving')
              : pendingRemoval.kind === 'member' ? t('team.removing')
                : t('team.cancelling')
          }
          cancelLabel={t('common.cancel')}
          onCancel={() => setPendingRemoval(null)}
          onConfirm={async () => {
            const target = pendingRemoval
            const done = target.kind === 'leave'
              ? t('team.leftToast', { project: target.name })
              : target.kind === 'member'
                ? t('team.removedToast', { name: target.name })
                : t('team.inviteCancelledToast', { name: target.name })
            /* El diálogo se cierra al terminar y no al pulsar: `ConfirmDialog`
               espera a que la promesa resuelva para enseñar «Quitando…», y
               cerrándolo antes esa espera no la vería nadie. */
            if (target.kind === 'leave') {
              /* Sin pasar por `act`: aquél recarga la lista al terminar, y tras
                 salirse esa consulta devuelve 403 —el proyecto ya no es tuyo—.
                 Se vería un error rojo justo después de una acción que salió
                 bien. Aquí se avisa y se sale; adónde, lo decide quien monta. */
              setBusyId(target.id)
              try {
                await removeTeamMember(projectId, target.id, token)
                showToast(done)
                setPendingRemoval(null)
                onLeft?.()
              } catch (err) {
                setError(translateServerError(err, t('team.actionFailed')))
                setBusyId(null)
              }
              return
            }

            await act(target.id, () => (
              target.kind === 'invite'
                ? cancelTeamInvite(projectId, target.id, token)
                : removeTeamMember(projectId, target.id, token)
            ))
            showToast(done)
            setPendingRemoval(null)
          }}
        />
      ) : null}
    </div>
  )
}
